import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { IMG, TICKER_ITEMS, BRANDS, HOME_STATS, WHATSAPP_LINK } from '../data/constants';
import { PRODUCTS } from '../data/products';
import { INDUSTRIES } from '../data/industries';
import { CtaBanner } from '../components/SharedComponents';
import { Package, Award, Wrench, Phone, Star, ArrowRight } from 'lucide-react';

const FEAT_ITEMS = [
  { Icon: Package, h: 'Pan-India Shipping', p: 'Fast delivery across all states' },
  { Icon: Award, h: 'Authorized Partners', p: 'Zebra, Honeywell, TSC & more' },
  { Icon: Wrench, h: 'AMC & Repair', p: 'All major brands serviced' },
  { Icon: Phone, h: 'Expert Support', p: 'Mon–Sat 9:30 AM – 6:30 PM' },
];

const Home: FC = () => (
  <>
    {/* HERO */}
    <section className="hero" style={{ padding: 0 }}>
      <div className="hero-inner">
        <div className="hero-content">
          <div className="hero-badge"><Star size={12} /> Trusted by 500+ Businesses Across India</div>
          <h1>Complete <em>Barcode &amp;<br />Label</em> Solutions</h1>
          <p className="hero-desc">From custom barcode labels and thermal printers to complete POS billing systems — DevBhoomi Labels delivers end-to-end identification solutions with expert after-sales support.</p>
          <div className="hero-btns">
            <Link to="/products" className="btn btn-primary">Explore Products <ArrowRight size={15} style={{ display: 'inline', verticalAlign: 'middle' }} /></Link>
            <Link to="/contact" className="btn btn-outline">Get a Free Quote</Link>
          </div>
          <div className="trust-row">
            <div className="trust-item"><span className="trust-dot" />Authorized Zebra Partner</div>
            <div className="trust-item"><span className="trust-dot" />Pan-India Shipping</div>
            <div className="trust-item"><span className="trust-dot" />AMC &amp; Repair Service</div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-img-grid">
            <div className="hero-img-box tall">
              <img src={IMG.heroPrinter} alt="Zebra Barcode Printers" onError={(e) => { (e.target as HTMLImageElement).style.background = '#1a3a5c'; }} />
              <div className="img-label">Zebra Barcode Printers</div>
            </div>
            <div className="hero-img-box">
              <img src={IMG.heroLabels} alt="Barcode Labels" onError={(e) => { (e.target as HTMLImageElement).style.background = '#1a3a5c'; }} />
              <div className="img-label">Barcode Labels</div>
            </div>
            <div className="hero-img-box">
              <img src={IMG.heroPOS} alt="Retail POS Systems" onError={(e) => { (e.target as HTMLImageElement).style.background = '#1a3a5c'; }} />
              <div className="img-label">Retail POS Systems</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* FEATURES STRIP */}
    <div className="feat-strip">
      <div className="container">
        <div className="feat-row">
          {FEAT_ITEMS.map(({ Icon, h, p }) => (
            <div className="feat-item" key={h}>
              <span className="feat-icon"><Icon size={26} color="var(--accent2)" /></span>
              <div><h4>{h}</h4><p>{p}</p></div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* TICKER */}
    <div className="ticker-wrap">
      <div className="ticker-flex">
        <span className="ticker-label">Most In-Demand</span>
        <div className="ticker-track">
          <div className="ticker-inner">
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => <span className="ticker-item" key={i}><b>{t}</b></span>)}
          </div>
        </div>
      </div>
    </div>

    {/* PRODUCTS */}
    <section style={{ background: '#fff' }}>
      <div className="container">
        <div className="section-head-row">
          <div><span className="section-tag">Our Products</span><h2 className="section-title">Complete Barcode Solutions</h2></div>
          <Link to="/products" className="see-all">View All <ArrowRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} /></Link>
        </div>
        <div className="grid-4">
          {PRODUCTS.map(p => (
            <Link key={p.id} to={p.path} className="prod-card">
              <div className="prod-img-wrap">
                <img
                  className="prod-img" src={p.image} alt={p.title}
                  onError={(e) => { (e.target as HTMLImageElement).style.background = '#f1f5f9'; }}
                />
                <span className={`prod-badge b-${p.badgeColor}`}>{p.badge}</span>
              </div>
              <div className="prod-body">
                <h3>{p.title}</h3><p>{p.description}</p>
                <div className="prod-foot"><span className="pcat">{p.category}</span><span className="parrow"><ArrowRight size={14} /></span></div>
              </div>
            </Link>
          ))}
          <Link to="/contact" className="prod-card cta-dark-card">
            <h3>Need Custom Labels?</h3>
            <p>Any size, material or quantity. Get a custom quote today.</p>
            <span className="btn btn-primary btn-sm">Get Quote <ArrowRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} /></span>
          </Link>
        </div>
      </div>
    </section>

    {/* INDUSTRIES */}
    <section style={{ background: 'var(--light)' }}>
      <div className="container">
        <div className="section-head-row">
          <div><span className="section-tag">Who We Serve</span><h2 className="section-title">Industries We Work With</h2></div>
          <Link to="/industries" className="see-all">All Industries <ArrowRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} /></Link>
        </div>
        <div className="grid-4">
          {INDUSTRIES.slice(0, 8).map(ind => (
            <Link key={ind.id} to={`/industries/${ind.id}`} className="ind-card">
              <img
                className="ind-card-img" src={ind.image} alt={ind.title}
                onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
              />
              <div className="ind-body"><h4>{ind.title}</h4><p>{ind.tags[0]}</p></div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* SERVICES */}
    <section style={{ background: '#fff' }}>
      <div className="container">
        <div className="section-head-row">
          <div><span className="section-tag">After-Sales Services</span><h2 className="section-title">We Support What We Sell</h2></div>
          <Link to="/services" className="see-all">All Services <ArrowRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} /></Link>
        </div>
        <div className="grid-2">
          <div className="svc-card">
            <img className="svc-card-img" src={IMG.amc} alt="AMC Services" onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }} />
            <div className="svc-card-body">
              <span className="svc-tag">Annual Maintenance Contract</span>
              <h3>Repair &amp; Maintenance (AMC)</h3>
              <p>Keep your barcode printers and scanners running at peak performance with our comprehensive AMC plans.</p>
              <ul><li>Preventive maintenance visits</li><li>Priority 24–48 hr on-site repair</li><li>All major brands covered</li><li>Genuine spare parts</li></ul>
              <Link to="/services" className="btn btn-primary btn-sm">Enquire for AMC <ArrowRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} /></Link>
            </div>
          </div>
          <div className="svc-card">
            <img className="svc-card-img" src={IMG.support} alt="Technical Support" onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }} />
            <div className="svc-card-body">
              <span className="svc-tag">Technical Support</span>
              <h3>Expert Technical Support</h3>
              <p>Remote and on-site support for all barcode hardware, software, label design and complete POS setups.</p>
              <ul><li>Remote desktop assistance</li><li>Label design &amp; ZPL programming</li><li>ERP &amp; software integration</li><li>Staff training provided</li></ul>
              <Link to="/services" className="btn btn-primary btn-sm">Request Support <ArrowRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} /></Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* STATS */}
    <div className="stats-strip">
      <div className="container">
        <div className="stats-grid">
          {HOME_STATS.map(s => (
            <div key={s.label} className="stat-item">
              <div className="stat-num">{s.number}</div><div className="stat-lbl">{s.label}</div><p className="stat-sub">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* BRANDS */}
    <section style={{ background: 'var(--light)', padding: '56px 0' }}>
      <div className="container" style={{ textAlign: 'center' }}>
        <span className="section-tag" style={{ textAlign: 'center' }}>Authorized Partners</span>
        <h2 className="section-title" style={{ textAlign: 'center' }}>Brands We Carry &amp; Service</h2>
        <p className="section-sub" style={{ textAlign: 'center', margin: '0 auto' }}>Authorized dealers and service partners for leading global barcode brands.</p>
        <div className="brands-row">{BRANDS.map(b => <div key={b} className="brand-chip">{b}</div>)}</div>
      </div>
    </section>

    <CtaBanner image={IMG.ctaWarehouse} title="Ready to Streamline Your Operations?" subtitle="Get in touch for a free consultation and custom pricing."
      primaryBtnText="Contact Us Now" primaryBtnLink="/contact"
      secondaryBtnText="WhatsApp" secondaryBtnLink={WHATSAPP_LINK} secondaryIsExternal />
  </>
);

export default Home;
